import React, { useState } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Users, Package, ShoppingCart, ClipboardList,
  Truck, CheckSquare, FileSpreadsheet, LogOut, Menu, Bell, Settings as SettingsIcon,
  FileText, ScrollText, MapPin, Building2, UsersRound, UserPlus, Network, IndianRupee
} from "lucide-react";
import { Button } from "@/components/ui/button";
import GlobalSearch from "@/components/GlobalSearch";

const GLOBAL_SEARCH_ROLES = [
  "admin", "accountant", "backend_executive", "market_developer",
  "distributor", "ibo", "sales",
];

// Icons re-used for Distribution nav
const DM_LINKS = {
  dashboard: { to: "/distribution", label: "DM Dashboard", icon: LayoutDashboard },
  territories: { to: "/distribution/territories", label: "Territories", icon: MapPin },
  distributors: { to: "/distribution/distributors", label: "Distributors", icon: Building2 },
  parties: { to: "/distribution/parties", label: "Parties", icon: UsersRound },
};

const NAV = {
  admin: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/orders/new", label: "Place Order", icon: ShoppingCart },
    { to: "/orders", label: "All Orders", icon: ClipboardList },
    { to: "/claims/new", label: "Claim Order", icon: CheckSquare },
    { to: "/claims", label: "Claim Orders", icon: ClipboardList },
    { to: "/notices", label: "Notices", icon: Bell },
    { to: "/b2b", label: "B2B Submissions", icon: FileText },
    { to: "/admin/ibo-applications", label: "IBO Applications", icon: UserPlus },
    { to: "/admin/ibo-genealogy", label: "IBO Genealogy", icon: Network },
    { to: "/users", label: "Users", icon: Users },
    { to: "/skus", label: "SKUs", icon: Package },
    { to: "/reports", label: "Reports", icon: FileSpreadsheet },
    { to: "/profits", label: "Profits", icon: IndianRupee },
    { to: "/audits", label: "Audit Log", icon: ScrollText },
    { to: "/settings", label: "Settings", icon: SettingsIcon },
    { section: "Distribution" },
    DM_LINKS.dashboard, DM_LINKS.territories, DM_LINKS.distributors, DM_LINKS.parties,
  ],
  subadmin: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/orders", label: "All Orders", icon: ClipboardList },
    { to: "/users", label: "Users", icon: Users },
    { to: "/skus", label: "SKUs", icon: Package },
    { to: "/reports", label: "Reports", icon: FileSpreadsheet },
    { to: "/settings", label: "Settings", icon: SettingsIcon },
  ],
  sales: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/orders/new", label: "Place Order", icon: ShoppingCart },
    { to: "/orders", label: "My Orders", icon: ClipboardList },
    { to: "/claims/new", label: "Claim Order", icon: CheckSquare },
    { to: "/claims", label: "My Claims", icon: ClipboardList },
    { to: "/reports", label: "Reports", icon: FileSpreadsheet },
  ],
  operations: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/orders", label: "Fulfillment", icon: Truck },
    { to: "/profits", label: "Profits", icon: IndianRupee },
    { to: "/reports", label: "Reports", icon: FileSpreadsheet },
  ],
  approval: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/claims", label: "Claim Approvals", icon: CheckSquare },
  ],
  accountant: [
    { to: "/b2b", label: "B2B Invoicing", icon: FileText },
    { section: "Distribution" },
    DM_LINKS.dashboard, DM_LINKS.distributors, DM_LINKS.parties,
  ],
  backend_executive: [
    DM_LINKS.dashboard, DM_LINKS.territories, DM_LINKS.distributors, DM_LINKS.parties,
  ],
  market_developer: [
    DM_LINKS.dashboard, DM_LINKS.territories, DM_LINKS.parties,
  ],
  distributor: [
    DM_LINKS.dashboard, DM_LINKS.parties,
  ],
  ibo: [
    DM_LINKS.dashboard, DM_LINKS.territories, DM_LINKS.distributors, DM_LINKS.parties,
    { to: "/b2b/my-bills", label: "B2B Bills", icon: FileText },
    { to: "/ibo/my-team", label: "My Team", icon: Network },
  ],
  profit_calculator: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/profits", label: "Profits", icon: IndianRupee },
  ],
};

const ROLE_LABEL = {
  admin: "Administrator",
  subadmin: "Sub-Admin (Read-only)",
  sales: "Sales BDM",
  operations: "Operations",
  approval: "Approval Team",
  accountant: "Accountant",
  backend_executive: "Backend Executive",
  market_developer: "Market Developer",
  distributor: "Distributor",
  ibo: "IBO",
  profit_calculator: "Profit Calculator",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Union of nav items across all roles the user holds (multi-role support).
  const roles = Array.isArray(user.roles) && user.roles.length > 0
    ? user.roles
    : [user.role];
  const items = React.useMemo(() => {
    const seen = new Set();
    const merged = [];
    let inSection = null;
    roles.forEach((r) => {
      (NAV[r] || []).forEach((it) => {
        if (it.section) {
          if (inSection !== it.section) {
            merged.push(it);
            inSection = it.section;
          }
          return;
        }
        if (seen.has(it.to)) return;
        seen.add(it.to);
        merged.push(it);
      });
    });
    return merged;
  }, [roles]);

  // Build a readable role label for the sidebar footer (all roles).
  const roleLabel = roles.map((r) => ROLE_LABEL[r] || r).join(" · ");

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="h-screen bg-background flex overflow-hidden">
      {/* Mobile overlay */}
      {open && (
        <div className="fixed inset-0 bg-black/30 z-30 md:hidden" onClick={() => setOpen(false)} />
      )}

      {/* Sidebar — fixed to viewport height so it never scrolls off with the content */}
      <aside
        data-testid="sidebar"
        className={`fixed md:static z-40 w-64 h-screen bg-white border-r border-border flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
      >
        <div className="h-16 flex items-center px-6 border-b border-border shrink-0">
          <span className="font-display font-bold text-lg tracking-tight">AOMS</span>
          <span className="ml-2 text-xs text-muted-foreground hidden lg:inline">Order Management</span>
        </div>
        <nav className="flex-1 min-h-0 py-4 px-3 space-y-1 overflow-y-auto">
          {items.map((it, idx) => {
            if (it.section) {
              return (
                <div key={`section-${idx}`} className="pt-3 pb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {it.section}
                </div>
              );
            }
            const Icon = it.icon;
            const active = location.pathname === it.to;
            return (
              <Link
                key={it.to}
                to={it.to}
                data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${active ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted"}`}
              >
                <Icon className="w-4 h-4" />
                {it.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border shrink-0">
          <div className="px-3 py-2">
            <div className="text-sm font-medium" data-testid="user-name">{user.name}</div>
            <div className="text-xs text-muted-foreground" data-testid="user-role">{roleLabel}</div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-sm"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> Log out
          </Button>
        </div>
      </aside>

      {/* Main column — has its OWN scroll container so the sidebar stays put */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        <header className="h-16 bg-white border-b border-border flex items-center px-4 md:px-8 shrink-0 z-20">
          <button
            className="md:hidden mr-3 text-foreground"
            onClick={() => setOpen(true)}
            data-testid="open-sidebar-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          <span className="font-display font-semibold text-base md:text-lg">
            BDM Order Management System
          </span>
          <div className="ml-auto flex items-center gap-3">
            {roles.some((r) => GLOBAL_SEARCH_ROLES.includes(r)) && (
              <GlobalSearch allowedRoles={GLOBAL_SEARCH_ROLES} />
            )}
          </div>
        </header>
        <main className="flex-1 min-h-0 overflow-y-auto p-4 md:p-8 max-w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
